import { useMemo } from 'react'
import { useIntlayer } from 'react-intlayer'
import { usePermission } from '@/features/permissions/hooks/use-permission'
import type { NavChild, NavItem } from '../domain/nav'

const useNavItems = (): NavItem[] => {
	const nav = useIntlayer('nav')

	const canViewMembers = usePermission('hr.members.view')
	const canViewTimeOff = usePermission('hr.time-off.view-own')
	const canViewWorkflows = usePermission('workflows.view')
	const canManagePermissions = usePermission('settings.permissions.manage')

	const hrChildren = useMemo(() => {
		const children = []
		if (canViewMembers) children.push({ path: '/hr', name: nav.hrTeam.value })
		if (canViewTimeOff) {
			children.push({ path: '/hr/time-off', name: nav.hrTimeOff.value })
		}
		return children
	}, [canViewMembers, canViewTimeOff, nav])

	const settingsChildren = useMemo(() => {
		const children: NavChild[] = [{ path: '/settings/members', name: nav.settingsMembers.value }]
		if (canManagePermissions) children.push({ path: '/settings/teams', name: nav.settingsTeams.value })
		return children
	}, [canManagePermissions, nav])

	return useMemo<NavItem[]>(() => {
		const items: NavItem[] = [
			{ path: '/', name: nav.overview.value },
			{ path: '/chat', name: nav.chat.value },
		]

		if (canViewWorkflows) {
			items.push({
				path: '/tasks',
				name: nav.tasks.value,
				children: [{ path: '/tasks/workflows', name: nav.tasksWorkflows.value }],
			})
		}

		items.push({ path: '/customers', name: nav.customers.value })
		items.push({ path: '/vault', name: nav.vault.value })
		items.push({ path: '/reports', name: nav.reports.value })

		if (hrChildren.length > 0) {
			items.push({ path: '/hr', name: nav.hr.value, children: hrChildren })
		}

		items.push({ path: '/settings', name: nav.settings.value, children: settingsChildren })

		return items
	}, [nav, canViewWorkflows, hrChildren, settingsChildren])
}

export { useNavItems }
